import { FaBook, FaChevronRight, FaMapMarkerAlt, FaPlus, FaTrash, FaVideo } from "react-icons/fa";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import Select from "../../../components/Select";
import dayjs from "dayjs";
import { FaCheck } from "react-icons/fa";

export const Step1SubjectTime = ({
  formData,
  subjects,
  loading,
  fieldErrors,
  updateForm,
  updateTimeSlot,
  addTimeSlot,
  removeTimeSlot,
  duplicateTimeSlot,
  canProceedToStep2,
  nextStep,
  getTimeSlotError,
  isFutureTimeSlot
}) => {
  return (
    <div className="space-y-6">
      <div className="border-l-4 border-l-primary pl-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FaBook className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-text">
            Select Subject & Schedule
          </h2>
        </div>
      </div>

      <Select
        label="Subject"
        value={formData.subjectId}
        onChange={(e) => updateForm({ subjectId: e.target.value })}
        options={[
          { value: "", label: "Select a subject" },
          ...subjects.map((s) => ({
            value: s._id,
            label: `${s.name} (${s.code})`,
          })),
        ]}
        error={fieldErrors.subject}
        required
        disabled={loading.subjects}
      />

      <div className="space-y-3">
        <label className="block text-sm font-semibold text-text">
          Session Type
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label
            className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer ${
              formData.sessionType === "online"
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
          >
            <input
              type="radio"
              name="sessionType"
              value="online"
              checked={formData.sessionType === "online"}
              onChange={(e) =>
                updateForm({
                  sessionType: e.target.value,
                  location: "",
                })
              }
              className="w-5 h-5 text-primary"
            />
            <div className="flex items-center gap-2 flex-1">
              <FaVideo className="text-primary w-5 h-5" />
              <div>
                <p className="font-medium text-text">Online</p>
                <p className="text-xs text-text/60">Via Google Meet</p>
              </div>
            </div>
          </label>

          <label
            className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer ${
              formData.sessionType === "in-person"
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
          >
            <input
              type="radio"
              name="sessionType"
              value="in-person"
              checked={formData.sessionType === "in-person"}
              onChange={(e) =>
                updateForm({
                  sessionType: e.target.value,
                })
              }
              className="w-5 h-5 text-primary"
            />
            <div className="flex items-center gap-2 flex-1">
              <FaMapMarkerAlt className="text-primary w-5 h-5" />
              <div>
                <p className="font-medium text-text">In-Person</p>
                <p className="text-xs text-text/60">Physical meeting</p>
              </div>
            </div>
          </label>
        </div>
      </div>

      {formData.sessionType === "in-person" && (
        <div className="animate-fadeIn">
          <Input
            label="Meeting Location"
            type="text"
            value={formData.location}
            onChange={(e) => updateForm({ location: e.target.value })}
            placeholder="e.g., Library Room 201"
            required
          />
        </div>
      )}

      <div className="space-y-4">
        <label className="block text-sm font-semibold text-text">
          Preferred Time Slots
        </label>

        <div className="space-y-4">
          {formData.preferredTimes.map((time, index) => {
            const error = getTimeSlotError(time);
            const isValid = isFutureTimeSlot(time);

            return (
              <div
                key={index}
                className="p-4 border border-border rounded-lg bg-surface/30 space-y-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text/70">
                    Time Slot {index + 1}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => duplicateTimeSlot(index)}
                      variant="ghost"
                      size="sm"
                      className="text-blue-500 hover:text-blue-600"
                      title="Duplicate time slot"
                    >
                      <FaPlus className="w-3.5 h-3.5" />
                    </Button>
                    {formData.preferredTimes.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeTimeSlot(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600"
                        title="Remove time slot"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Input
                    type="date"
                    value={time.date}
                    onChange={(e) =>
                      updateTimeSlot(index, "date", e.target.value)
                    }
                    min={dayjs().format("YYYY-MM-DD")}
                    required
                    error={fieldErrors[`timeSlot${index}`]}
                  />
                  <Input
                    type="time"
                    value={time.start}
                    onChange={(e) =>
                      updateTimeSlot(index, "start", e.target.value)
                    }
                    required
                  />
                  <Input
                    type="time"
                    value={time.end}
                    onChange={(e) =>
                      updateTimeSlot(index, "end", e.target.value)
                    }
                    required
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                    <HiOutlineExclamationTriangle className="flex-shrink-0 w-4 h-4" />
                    {error}
                  </div>
                )}
                {!error && isValid && (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
                    <FaCheck className="flex-shrink-0 w-4 h-4" />
                    Valid time slot
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Button
          type="button"
          onClick={addTimeSlot}
          variant="outline"
          size="sm"
          className="w-full sm:w-auto"
          disabled={formData.preferredTimes.length >= 5}
        >
          Add Another Time Slot
        </Button>
      </div>

      <div className="flex justify-end pt-4">
        <Button
          type="button"
          onClick={nextStep}
          disabled={!canProceedToStep2}
          className="w-full sm:w-auto"
        >
          Continue
          <FaChevronRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
